import { isCommercialTermsProductOnly, isCommissionProductOnly,
  isFixedOneTimeProductOnly,
  isFixedPerUnitProductOnly, isInstallmentsProductOnly,
  isRefundProductOnly,
  isSubscriptionProductOnly } from '@weft/services';
import type { Currency, FixedProduct, FixedProductInput, PlanBillingData } from '@weft/types';
import { raise } from '@weft/utils';
import React from 'react';
import { CommercialTermsChargeEdit } from './CommercialTermsCharge/CommercialTermsChargeEdit';
import { CommercialTermsChargeView } from './CommercialTermsCharge/CommercialTermsChargeView';
import { CommissionChargeEdit } from './CommissionCharge/CommissionChargeEdit';
import { CommissionChargeView } from './CommissionCharge/CommissionChargeView';
import { FixedOneTimeChargeEdit } from './FixedOneTimeCharge/FixedOneTimeChargeEdit';
import { FixedOneTimeChargeView } from './FixedOneTimeCharge/FixedOneTimeChargeView';
import { FixedPerUnitChargeEdit } from './FixedPerUnitCharge/FixedPerUnitChargeEdit';
import { FixedPerUnitChargeView } from './FixedPerUnitCharge/FixedPerUnitChargeView';
import { InstallmentsChargeEdit } from './InstallmentsCharge/InstallmentsChargeEdit';
import { InstallmentsChargeView } from './InstallmentsCharge/InstallmentsChargeView';
import { SubscriptionChargeEdit } from './SubscriptionCharge/SubscriptionChargeEdit';
import { SubscriptionChargeView } from './SubscriptionCharge/SubscriptionChargeView';

type FixedChargeProps = {
  product: FixedProduct | FixedProductInput;
  billingData: PlanBillingData;
  productIndex: number;
  editable: boolean;
  currency: Currency;
};

export const FixedCharge: React.FC<FixedChargeProps> = ({ product, editable, billingData }) => {
  const createFixedCharge = useFixedChargeFactory(billingData);

  return createFixedCharge(product, editable);
};

function useFixedChargeFactory(billingData: PlanBillingData) {
  return (product: FixedProduct | FixedProductInput, editable: boolean) => {
    if (isFixedOneTimeProductOnly(product) || isRefundProductOnly(product)) {
      if (editable) {
        return <FixedOneTimeChargeEdit/>;
      }

      return <FixedOneTimeChargeView/>;
    }

    if (isSubscriptionProductOnly(product)) {
      if (editable) {
        return <SubscriptionChargeEdit/>;
      }

      return <SubscriptionChargeView billingInterval={billingData.billingInterval}/>;
    }

    if (isCommissionProductOnly(product)) {
      if (editable) {
        return <CommissionChargeEdit/>;
      }

      return <CommissionChargeView/>;
    }

    if (isFixedPerUnitProductOnly(product)) {
      if (editable) {
        return <FixedPerUnitChargeEdit/>;
      }

      return <FixedPerUnitChargeView billingInterval={billingData.billingInterval}/>;
    }

    if (isInstallmentsProductOnly(product)) {
      if (editable) {
        return <InstallmentsChargeEdit/>;
      }

      return <InstallmentsChargeView/>;
    }

    if (isCommercialTermsProductOnly(product)) {
      if (editable) {
        return <CommercialTermsChargeEdit/>;
      }

      return <CommercialTermsChargeView/>;
    }

    return raise(`Unknown fixed product type: ${JSON.stringify(product, null, 2)}`);
  };
}